/**
 * EGY KÉRÉS — a makett (`design/Ugyfel.dc.html`) két kártyája egy komponensben.
 *
 * A KÉT ÁG UGYANAZ A SOR, MÁS MONDATTAL. A megnevezett számla MEGNEVEZ (a
 * partner, a szám, az összeg, a kelte), a bankos kérdés KÉRDEZ — de a
 * bizonyosság szava egyikben sem szerepel (`2.5b`), mert a szerver el sem
 * küldi.
 *
 * ⚠️ AMI A MAKETTBŐL SZÁNDÉKOSAN KIMARAD: a „Nem találom" és a „Nincs számla"
 * gomb. Mindkettő ÍRÁS a hiánytétel állapotgépébe — az a §8c 9–10. lépése.
 * Egy gomb, ami szépen néz ki és nem csinál semmit, rosszabb, mint a hiánya:
 * az ügyfél azt hinné, válaszolt.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CameraIcon, FileIcon } from '@/components/common';
import { useAuth } from '@/features/auth/AuthProvider';
import { CameraSheet } from '@/features/capture/CameraSheet';
import { captureKey, capturePrincipal } from '@/features/capture/session-storage';
import { FilePicker } from '@/features/uploads/FilePicker';
import { GroupSheet } from '@/features/uploads/GroupSheet';
import { needsGrouping } from '@/features/uploads/grouping';
import { formatAmount, formatDate } from '@/i18n/generated';
import type { ClientRequest } from './types';

interface RequestCardProps {
  request: ClientRequest;
  busy: boolean;
  /**
   * LISTÁT KAP, de ez a két gomb egyet ad. A kártya EGY megnevezett számláról
   * szól — a köteg a lábsáv ejtőmezőjéé, ahol nincs, amihez hozzárendelni.
   */
  onPick: (files: File[]) => void;
  /**
   * A KAMERÁBÓL JÖVŐ SOROZAT — egy bizonylat oldalai, ebben a sorrendben.
   *
   * Külön a `onPick`-től, mert MÁS a szerződése: a fájlválasztóból érkező
   * lista fájlonként egy bizonylat, ez pedig EGY bizonylat N oldala. A kettőt
   * egy visszahívásba fogni azt jelentené, hogy a hívónak kell kitalálnia,
   * melyikről van szó — és egyszer rosszul találná ki.
   */
  onPages: (pages: Blob[]) => void;
  /**
   * TÖBB GALÉRIAKÉP (#50) — csoportonként egy bizonylat. A kártyáról MIND
   * EGYBEN indul: a kártya egy megnevezett számláról szól, a több kép szinte
   * biztosan annak az oldala. Ha mégsem, az áttekintőn szétválasztja.
   */
  onGroups: (groups: File[][]) => void;
}

export function RequestCard({ request, busy, onPick, onPages, onGroups }: RequestCardProps) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [cameraOpen, setCameraOpen] = useState(false);
  const named = request.kind === 'NAMED_INVOICE';

  return (
    <article className="card">
      <p className="eyebrow">{t(named ? 'requests.namedInvoice' : 'requests.bankQuestion')}</p>

      <div className="request-facts">
        {request.partnerName ? <p className="partner">{request.partnerName}</p> : null}

        {request.invoiceNumber || request.issueDate ? (
          <p className="num meta">
            {[request.invoiceNumber, request.issueDate ? formatDate(request.issueDate) : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}

        {request.amount ? (
          // A pénz SZÖVEGKÉNT utazik, és csak itt lesz `number` — egyetlen
          // helyen, a formázás pillanatában.
          <p className="num amount">{formatAmount(Number(request.amount))}</p>
        ) : null}

        {named ? null : <p className="meta">{t('requests.bankQuestionBody')}</p>}

        <p className="meta">{t('requests.askedOn', { date: formatDate(request.askedAt) })}</p>
      </div>

      <div className="request-actions">
        {/* A FOTÓZÁS MŰVELET, NEM OLDAL (`CLAUDE.md` §2): a kamera egy réteg
            a hiánylista fölött, nem útvonal — bezárva ide tér vissza. */}
        {/* KÉT CSEMPE, DE NEM EGYENRANGÚ (#57). A fotózás a kiemelt — a papír
            az ügyfél kezében van —, ezért az kapja az akció-színű kitöltést; a fájl
            a második út, keretes csempén. */}
        <button
          type="button"
          className="pick pick-tile pick-tile-primary"
          disabled={busy}
          onClick={() => setCameraOpen(true)}
        >
          <span className="pick-icon">
            <CameraIcon />
          </span>
          <span>{t('upload.choosePhoto')}</span>
        </button>
        <CardFilePicker busy={busy} onPick={onPick} onGroups={onGroups} />
      </div>

      {cameraOpen ? (
        <CameraSheet
          // A MEGŐRZÉS KULCSA A KÉRÉS AZONOSÍTÓJA (Sz6). Kérésenként külön:
          // két számla félkész sorozata nem keveredhet, és a „folytatod?"
          // kérdés is csak arra a kérésre vonatkozik, amit épp megnyitott.
          // ÉS AZ IDENTITÁSÉ (#76): ugyanazon a gépen a maszkolt könyvelő
          // nem folytathatja az ügyfél félbehagyott fotózását, és fordítva.
          storageKey={me ? captureKey(capturePrincipal(me.actor), request.id) : null}
          // A FEJLÉC VÉGIG MUTATJA, MIT KÉRTEK. Aki három számlát fotóz
          // egymás után, a harmadiknál már nem emlékszik, melyiket kezdte el.
          title={
            request.partnerName ?? t(named ? 'requests.namedInvoice' : 'requests.bankQuestion')
          }
          subtitle={request.invoiceNumber ?? undefined}
          onCancel={() => setCameraOpen(false)}
          onDone={(pages) => {
            setCameraOpen(false);
            onPages(pages);
          }}
        />
      ) : null}
    </article>
  );
}

/**
 * A KÁRTYA FÁJLVÁLASZTÓJA — TÖBB KÉP IS MEHET (#50).
 *
 * Egy kétoldalas számla két fotója: ma ezen a kártyán csak egy fájl volt
 * választható, és a második oldalnak nem volt helye. Több kép után az
 * összefűző áttekintő jön, MIND EGYBEN indulva — a kártya egy megnevezett
 * számláról szól. Egy kép vagy PDF a mai úton megy.
 */
function CardFilePicker({
  busy,
  onPick,
  onGroups,
}: Pick<RequestCardProps, 'busy' | 'onPick' | 'onGroups'>) {
  const { t } = useTranslation();
  const [grouping, setGrouping] = useState<File[] | null>(null);

  return (
    <>
      <FilePicker
        label={t('upload.chooseFile')}
        variant="tile"
        icon={<FileIcon />}
        disabled={busy}
        multiple
        onPick={(files) => {
          if (needsGrouping(files)) {
            setGrouping(files);
            return;
          }
          onPick(files);
        }}
      />
      {grouping === null ? null : (
        <GroupSheet
          files={grouping}
          together
          onCancel={() => setGrouping(null)}
          onSend={(groups) => {
            setGrouping(null);
            onGroups(groups);
          }}
        />
      )}
    </>
  );
}
